import React, { useState, useEffect, useMemo } from 'react';
import { timeTrackingApi } from '../../services/timeTrackingApi';
import api from '../../services/api';
import { FiCalendar, FiClock, FiUsers, FiCamera, FiEye, FiDownload, FiBarChart2, FiTrendingUp } from 'react-icons/fi';

const AdminMonthlyReportPage = () => {
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [employeeDropdownOpen, setEmployeeDropdownOpen] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [monthlySessionsCache, setMonthlySessionsCache] = useState({});
  const [loading, setLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [loadingStates, setLoadingStates] = useState({});
  const [selectedScreenshots, setSelectedScreenshots] = useState([]);
  const [showScreenshotModal, setShowScreenshotModal] = useState(false);

  // Load employees once on mount
  useEffect(() => {
    loadEmployees();
  }, []);

  // Only load data when individual employee is selected
  useEffect(() => {
    if (selectedEmployee !== 'all') {
      loadMonthlyDataIfNeeded();
    } else {
      // Reset data loaded state when switching back to 'all'
      setDataLoaded(false);
    }
  }, [selectedMonth, selectedEmployee]);

  const loadEmployees = async (search = '') => {
    try {
      const response = await api.get('/users', { params: { search, role: '', limit: 50, page: 1 } });
      let userList = Array.isArray(response.data) ? response.data : (response.data.users || response.data.data || []);
      setEmployees(userList.filter(user => user.role === 'employee' || user.role === 'manager'));
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const loadMonthlyDataIfNeeded = async () => {
    const cacheKey = selectedMonth;
    
    // Check if data is already cached or currently loading
    if (monthlySessionsCache[cacheKey] || loadingStates[cacheKey]) {
      setDataLoaded(true);
      return;
    }

    // Mark as loading
    setLoadingStates(prev => ({ ...prev, [cacheKey]: true }));
    setLoading(true);
    
    try {
      const year = selectedMonth.split('-')[0];
      const month = selectedMonth.split('-')[1];
      const daysInMonth = new Date(year, month, 0).getDate();
      
      // Create a single batch promise for all days
      const allPromises = [];
      for (let day = 1; day <= daysInMonth; day++) {
        const date = `${year}-${month.padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
        allPromises.push(
          timeTrackingApi.getAdminSessionsByDate(date)
            .then(response => ({ date, sessions: response.data.sessions || [] }))
            .catch(() => ({ date, sessions: [] }))
        );
      }
      
      // Execute all API calls in parallel
      const results = await Promise.all(allPromises);
      
      // Build the sessions map
      const allSessions = new Map();
      results.forEach(({ date, sessions }) => {
        allSessions.set(date, sessions);
      });
      
      // Cache the data
      setMonthlySessionsCache(prev => ({ ...prev, [cacheKey]: allSessions }));
      setDataLoaded(true);
      
    } catch (error) {
      console.error('Error loading monthly data:', error);
    } finally {
      // Clear loading state
      setLoadingStates(prev => ({ ...prev, [cacheKey]: false }));
      setLoading(false);
    }
  };

  const loadDataForOverview = () => {
    loadMonthlyDataIfNeeded();
  };

  // Memoized report data calculation
  const reportData = useMemo(() => {
    const cacheKey = selectedMonth;
    const monthlyData = monthlySessionsCache[cacheKey];
    
    if (!monthlyData) return [];

    const employeesToAnalyze = selectedEmployee === 'all' 
      ? employees 
      : employees.filter(emp => emp.id.toString() === selectedEmployee.toString());

    return employeesToAnalyze.map(employee => {
      let totalSeconds = 0;
      let activeSeconds = 0;
      let idleSeconds = 0;
      let workingDays = 0;
      let screenshots = 0;
      const dailyData = [];
      const projects = {};

      // Process all days for this employee
      monthlyData.forEach((sessions, date) => {
        const employeeSessions = sessions.filter(session => 
          session.user_id === employee.id || 
          session.User?.id === employee.id ||
          session.user_id?.toString() === employee.id?.toString()
        );

        let dayTotal = 0;
        let dayActive = 0;
        let dayIdle = 0;
        let dayScreenshots = 0;

        employeeSessions.forEach(session => {
          dayTotal += session.total_seconds || 0;
          dayActive += session.active_seconds || 0;
          dayIdle += session.idle_seconds || 0;
          
          if (session.screenshots?.length) {
            dayScreenshots += session.screenshots.length;
          }

          // Only process projects for individual view
          if (selectedEmployee !== 'all') {
            const projectKey = session.project_name || 'No Project';
            if (!projects[projectKey]) {
              projects[projectKey] = { name: projectKey, totalSeconds: 0, activeSeconds: 0, sessions: 0 };
            }
            projects[projectKey].totalSeconds += session.total_seconds || 0;
            projects[projectKey].activeSeconds += session.active_seconds || 0;
            projects[projectKey].sessions++;
          }
        });

        if (dayTotal > 0) workingDays++;
        totalSeconds += dayTotal;
        activeSeconds += dayActive;
        idleSeconds += dayIdle;
        screenshots += dayScreenshots;

        // Only store daily data for individual view
        if (selectedEmployee !== 'all') {
          dailyData.push({
            date,
            totalSeconds: dayTotal,
            activeSeconds: dayActive,
            idleSeconds: dayIdle,
            screenshots: dayScreenshots,
            productivity: dayTotal > 0 ? Math.round((dayActive / dayTotal) * 100) : 0
          });
        }
      });

      return {
        employee,
        totalSeconds,
        activeSeconds,
        idleSeconds,
        workingDays,
        screenshots,
        productivity: totalSeconds > 0 ? Math.round((activeSeconds / totalSeconds) * 100) : 0,
        dailyData: selectedEmployee !== 'all' ? dailyData : [],
        projects: selectedEmployee !== 'all' ? Object.values(projects) : [],
        avgDailyHours: workingDays > 0 ? (totalSeconds / 3600) / workingDays : 0
      };
    });
  }, [monthlySessionsCache, selectedMonth, employees, selectedEmployee]);

  // Memoized summary data
  const summaryData = useMemo(() => {
    const totalHours = reportData.reduce((sum, report) => sum + (report.totalSeconds / 3600), 0);
    const totalScreenshots = reportData.reduce((sum, report) => sum + report.screenshots, 0);
    const employeesWithData = reportData.filter(report => report.totalSeconds > 0);
    const avgProductivity = employeesWithData.length > 0 
      ? Math.round(employeesWithData.reduce((sum, report) => sum + report.productivity, 0) / employeesWithData.length)
      : 0;

    return {
      totalEmployees: reportData.length,
      totalHours: Math.round(totalHours * 100) / 100,
      totalScreenshots,
      avgProductivity
    };
  }, [reportData]);

  const loadEmployeeScreenshots = async (employeeId, employeeName) => {
    const cacheKey = selectedMonth;
    const monthlyData = monthlySessionsCache[cacheKey];
    
    if (!monthlyData) return;

    setLoading(true);
    try {
      const screenshots = [];
      
      monthlyData.forEach((sessions, date) => {
        const employeeSessions = sessions.filter(session => 
          session.user_id === employeeId || session.User?.id === employeeId
        );

        employeeSessions.forEach(session => {
          if (session.screenshots?.length) {
            session.screenshots.forEach(screenshot => {
              screenshots.push({
                ...screenshot,
                date,
                sessionId: session.id,
                projectName: session.project_name,
                taskTitle: session.task_title
              });
            });
          }
        });
      });

      setSelectedScreenshots({ employeeName, screenshots });
      setShowScreenshotModal(true);
    } catch (error) {
      console.error('Error loading employee screenshots:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const formatHours = (hours) => `${hours.toFixed(1)}h`;

  const getProductivityColor = (productivity) => {
    if (productivity >= 80) return 'text-green-600';
    if (productivity >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getProductivityBg = (productivity) => {
    if (productivity >= 80) return 'bg-green-100';
    if (productivity >= 60) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  const exportToCSV = () => {
    const headers = ['Employee', 'Total Hours', 'Active Hours', 'Idle Hours', 'Productivity %', 'Working Days', 'Screenshots', 'Avg Daily Hours'];
    const csvContent = [
      headers.join(','),
      ...reportData.map(report => [
        report.employee.name,
        (report.totalSeconds / 3600).toFixed(2),
        (report.activeSeconds / 3600).toFixed(2),
        (report.idleSeconds / 3600).toFixed(2),
        report.productivity,
        report.workingDays,
        report.screenshots,
        report.avgDailyHours.toFixed(2)
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `employee-time-report-${selectedMonth}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Employee Time Tracking Report</h1>
        <div className="flex items-center space-x-4">
          <button
            onClick={exportToCSV}
            disabled={!dataLoaded || reportData.length === 0}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiDownload className="mr-2" size={16} />
            Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center">
            <FiCalendar className="mr-2 text-gray-500" />
            <label className="block text-sm font-medium text-gray-700 mr-2">Month:</label>
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2"
            />
          </div>
          <div className="flex items-center">
            <FiUsers className="mr-2 text-gray-500" />
            <label className="block text-sm font-medium text-gray-700 mr-2">Employee:</label>
            <div className="relative min-w-64">
              <input
                type="text"
                value={employeeSearch}
                onChange={(e) => {
                  setEmployeeSearch(e.target.value);
                  setEmployeeDropdownOpen(true);
                  loadEmployees(e.target.value);
                }}
                onFocus={() => setEmployeeDropdownOpen(true)}
                onBlur={() => setTimeout(() => setEmployeeDropdownOpen(false), 200)}
                placeholder="Search employee..."
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
              {employeeDropdownOpen && (
                <div className="absolute z-10 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto mt-1">
                  <button
                    type="button"
                    onMouseDown={() => { setSelectedEmployee('all'); setEmployeeSearch(''); setEmployeeDropdownOpen(false); }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${selectedEmployee === 'all' ? 'bg-blue-50 text-blue-700' : ''}`}
                  >
                    All Employees ({employees.length})
                  </button>
                  {employees
                    .filter(emp => emp.name.toLowerCase().includes(employeeSearch.toLowerCase()))
                    .map(emp => (
                      <button
                        key={emp.id}
                        type="button"
                        onMouseDown={() => { setSelectedEmployee(emp.id.toString()); setEmployeeSearch(emp.name); setEmployeeDropdownOpen(false); }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${selectedEmployee === emp.id.toString() ? 'bg-blue-50 text-blue-700' : ''}`}
                      >
                        {emp.name} <span className="text-gray-400 text-xs">({emp.role})</span>
                      </button>
                    ))
                  }
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards - Show basic info even without data */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100 text-blue-600">
              <FiUsers size={24} />
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-500">Total Employees</h3>
              <p className="text-2xl font-semibold text-gray-900">
                {selectedEmployee === 'all' ? employees.length : 1}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100 text-green-600">
              <FiClock size={24} />
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-500">Total Hours</h3>
              <p className="text-2xl font-semibold text-gray-900">
                {dataLoaded ? formatHours(summaryData.totalHours) : '--'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100 text-purple-600">
              <FiTrendingUp size={24} />
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-500">Avg Productivity</h3>
              <p className={`text-2xl font-semibold ${dataLoaded ? getProductivityColor(summaryData.avgProductivity) : 'text-gray-900'}`}>
                {dataLoaded ? `${summaryData.avgProductivity}%` : '--'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-orange-100 text-orange-600">
              <FiCamera size={24} />
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-500">Screenshots</h3>
              <p className="text-2xl font-semibold text-gray-900">
                {dataLoaded ? summaryData.totalScreenshots : '--'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Employee Reports Table */}
      {selectedEmployee === 'all' ? (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold">Employee Performance Overview</h3>
              {!dataLoaded && (
                <button
                  onClick={loadDataForOverview}
                  disabled={loading}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Loading...
                    </>
                  ) : (
                    'Load Performance Data'
                  )}
                </button>
              )}
            </div>
            
            {!dataLoaded && !loading ? (
              <div className="text-center py-16">
                <FiBarChart2 size={64} className="mx-auto mb-4 text-gray-300" />
                <h4 className="text-lg font-medium text-gray-700 mb-2">Performance Data Not Loaded</h4>
                <p className="text-gray-500 mb-6">Click "Load Performance Data" to view detailed analytics for all employees</p>
                <p className="text-sm text-gray-400">
                  This will fetch data for {new Date(selectedMonth + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </p>
              </div>
            ) : loading ? (
              <div className="text-center py-16">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-lg text-gray-600 mb-2">Loading employee performance data...</p>
                <p className="text-sm text-gray-500">
                  Fetching data for {new Date(selectedMonth + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Employee
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total Time
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Active Time
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Productivity
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Working Days
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Avg Daily
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Screenshots
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {reportData.map((report, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-medium">
                              {report.employee.name?.split(' ').map(n => n[0]).join('') || 'U'}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{report.employee.name}</div>
                              <div className="text-sm text-gray-500">{report.employee.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div>{formatTime(report.totalSeconds)}</div>
                          <div className="text-xs text-gray-500">
                            Idle: {formatTime(report.idleSeconds)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                          {formatTime(report.activeSeconds)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getProductivityBg(report.productivity)} ${getProductivityColor(report.productivity)}`}>
                            {report.productivity}%
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {report.workingDays} days
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatHours(report.avgDailyHours)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="flex items-center">
                            <FiCamera className="mr-1 text-gray-400" size={14} />
                            {report.screenshots}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => loadEmployeeScreenshots(report.employee.id, report.employee.name)}
                            className="text-blue-600 hover:text-blue-900 mr-3"
                          >
                            <FiEye size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Individual Employee Detailed View */
        loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-500">Loading employee details...</p>
          </div>
        ) : reportData.length > 0 && (
          <>
            {/* Individual Employee Details */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-xl">
                    {reportData[0].employee.name?.split(' ').map(n => n[0]).join('') || 'U'}
                  </div>
                  <div className="ml-6">
                    <h2 className="text-2xl font-bold text-gray-900">{reportData[0].employee.name}</h2>
                    <p className="text-gray-600">{reportData[0].employee.email}</p>
                    <p className="text-sm text-gray-500 capitalize">{reportData[0].employee.role}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-blue-600">
                    {formatTime(reportData[0].totalSeconds)}
                  </div>
                  <div className="text-sm text-gray-500">Total Time This Month</div>
                </div>
              </div>
            </div>

            {/* Individual Employee Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
              <div className="bg-white rounded-lg shadow p-4">
                <div className="text-2xl font-bold text-green-600">{formatTime(reportData[0].activeSeconds)}</div>
                <div className="text-sm text-gray-500">Active Time</div>
              </div>
              <div className="bg-white rounded-lg shadow p-4">
                <div className="text-2xl font-bold text-red-600">{formatTime(reportData[0].idleSeconds)}</div>
                <div className="text-sm text-gray-500">Idle Time</div>
              </div>
              <div className="bg-white rounded-lg shadow p-4">
                <div className={`text-2xl font-bold ${getProductivityColor(reportData[0].productivity)}`}>
                  {reportData[0].productivity}%
                </div>
                <div className="text-sm text-gray-500">Productivity</div>
              </div>
              <div className="bg-white rounded-lg shadow p-4">
                <div className="text-2xl font-bold text-purple-600">{reportData[0].workingDays}</div>
                <div className="text-sm text-gray-500">Working Days</div>
                <div className="text-xs text-gray-400">of {new Date(selectedMonth.split('-')[0], selectedMonth.split('-')[1], 0).getDate()} total</div>
              </div>
            </div>

            {/* Daily Breakdown Chart */}
            <div className="bg-white rounded-lg shadow mb-6">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-semibold">Daily Time Breakdown</h3>
                  <button
                    onClick={() => loadEmployeeScreenshots(reportData[0].employee.id, reportData[0].employee.name)}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    <FiCamera className="mr-2" size={16} />
                    View Screenshots ({reportData[0].screenshots})
                  </button>
                </div>
                <div className="space-y-2">
                  {reportData[0].dailyData.map((day, index) => {
                    const maxHours = Math.max(...reportData[0].dailyData.map(d => d.totalSeconds / 3600));
                    return (
                      <div key={index} className="flex items-center py-2">
                        <div className="w-20 text-sm font-medium text-gray-600">
                          {day.date.split('-')[2]} {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}
                        </div>
                        <div className="flex-1 mx-4">
                          <div className="flex items-center h-8">
                            <div 
                              className="bg-green-200 h-6 rounded-l flex items-center justify-center text-xs font-medium text-green-800"
                              style={{ width: `${maxHours > 0 ? (day.activeSeconds / 3600 / maxHours) * 100 : 0}%` }}
                            >
                              {day.activeSeconds > 0 && formatTime(day.activeSeconds)}
                            </div>
                            <div 
                              className="bg-red-200 h-6 rounded-r flex items-center justify-center text-xs font-medium text-red-800"
                              style={{ width: `${maxHours > 0 ? (day.idleSeconds / 3600 / maxHours) * 100 : 0}%` }}
                            >
                              {day.idleSeconds > 0 && formatTime(day.idleSeconds)}
                            </div>
                          </div>
                        </div>
                        <div className="w-20 text-right">
                          <div className="text-sm font-medium">{formatTime(day.totalSeconds)}</div>
                          <div className={`text-xs ${getProductivityColor(day.productivity)}`}>
                            {day.productivity}%
                          </div>
                        </div>
                        <div className="w-16 text-right text-sm text-gray-500">
                          📸 {day.screenshots}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center justify-center mt-4 text-xs text-gray-500">
                  <div className="flex items-center mr-4">
                    <div className="w-3 h-3 bg-green-200 rounded mr-1"></div>
                    Active Time
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-red-200 rounded mr-1"></div>
                    Idle Time
                  </div>
                </div>
              </div>
            </div>

            {/* Project-wise Breakdown */}
            {reportData[0].projects.length > 0 && (
              <div className="bg-white rounded-lg shadow">
                <div className="p-6">
                  <h3 className="text-lg font-semibold mb-6">Project-wise Time Allocation</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead>
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Project
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Total Time
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Active Time
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Productivity
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Sessions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {reportData[0].projects.map((project, index) => {
                          const projectProductivity = project.totalSeconds > 0 
                            ? Math.round((project.activeSeconds / project.totalSeconds) * 100) 
                            : 0;
                          
                          return (
                            <tr key={index}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {project.name}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatTime(project.totalSeconds)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                                {formatTime(project.activeSeconds)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getProductivityBg(projectProductivity)} ${getProductivityColor(projectProductivity)}`}>
                                  {projectProductivity}%
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {project.sessions}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </>
        )
      )}

      {/* Screenshot Modal */}
      {showScreenshotModal && selectedScreenshots && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-full overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">
                  Screenshots for {selectedScreenshots.employeeName} - {selectedMonth}
                </h3>
                <button
                  onClick={() => setShowScreenshotModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ×
                </button>
              </div>
              
              {selectedScreenshots.screenshots.length === 0 ? (
                <p className="text-center py-8 text-gray-500">No screenshots found for this employee in the selected month.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {selectedScreenshots.screenshots.map((screenshot, index) => (
                    <div key={index} className="border rounded-lg p-4">
                      <img
                        src={screenshot.screenshot_path || screenshot.image_url}
                        alt={`Screenshot ${index + 1}`}
                        className="w-full h-40 object-cover rounded mb-2"
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEyMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg==';
                        }}
                      />
                      <div className="text-xs text-gray-600">
                        <div><strong>Date:</strong> {screenshot.date}</div>
                        <div><strong>Time:</strong> {new Date(screenshot.taken_at).toLocaleTimeString()}</div>
                        {screenshot.projectName && <div><strong>Project:</strong> {screenshot.projectName}</div>}
                        {screenshot.taskTitle && <div><strong>Task:</strong> {screenshot.taskTitle}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMonthlyReportPage;